import styled from "@emotion/styled";
import { Divider, Drawer, message, Spin } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DrawTitle } from "screens/forms/form-utils";
import {
  OrderHistoryTitle,
  OrderListDatas,
  orderListEmpty,
  orderListTip,
  paymentTypeMap,
} from "screens/register/contant";
import { CloseOutlinedBox } from "screens/register/page-drawer-join";
import closeIcon from "assets/closeIcon.png";
import { LabelType } from "screens/register/page-header";
import { DisplayText } from "components/lib";
import { LoadingOutlined } from "@ant-design/icons";
import { getErrorMsg } from "utils/http";
import {
  MIX_PANEL_EVENT,
  trackMixPanel,
  useTrackCommonData,
} from "mixpanel/mixpanelTrackUtil";
import dayjs from "dayjs";
import { useLoyaltyMemberService } from "hook/useLoyaltyMemberServiceHook";
import { useAuth } from "context/auth-context";
import { RestaurantInfo } from "types/loyalty";

type subItemType = keyof typeof OrderListDatas;
type subItemKeyType = {
  [key in subItemType]?: string;
};
type paymentKeyType = keyof typeof paymentTypeMap;

const ORDER_TYPE = "order";
const PAY_TYPE = "prePaied2";

export const OrderList = (props: {
  lang: LabelType;
  clickCount: number;
  resetCardCount: () => void;
  uuid: string;
  restaurantNames: { [key in LabelType]: string };
}) => {
  const trackCommonData = useTrackCommonData();
  const { fetchLoyaltyOrderAndTranscationList } = useLoyaltyMemberService();
  const { restaurantGroupInfo, restaurant } = useAuth();
  // 总数据
  const [orderListTotal, setOrderTotalList] = useState([] as subItemKeyType[]);
  // 当前数据
  const [currentList, setCurrentList] = useState([] as subItemKeyType[]);
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotal] = useState(-1);
  const pageSize = 10;

  const contentRef = useRef(null);
  const [bottomVisible, setBottomVisible] = useState(false);


  const includRestaurantList = useMemo(() => {
    if (restaurantGroupInfo && restaurantGroupInfo.id && restaurantGroupInfo.includRestaurantList) {
      return restaurantGroupInfo.includRestaurantList;
    }
    return null;
  }, [restaurantGroupInfo]);

  const loginedRestaurant = useMemo(() => {
    return {
      id: restaurant.data.id,
      name: restaurant.data.attributes.name,
      foreign_name: restaurant.data.attributes.foreign_name,
    }
  }, [restaurant]);

  const getOrderBindRestaurant = useCallback((restaurant_id: string) => {
    return (includRestaurantList || []).find((item: RestaurantInfo) => (item.id + '') === restaurant_id) || loginedRestaurant;
  }, [loginedRestaurant, includRestaurantList]);

  const styleObj = (keyName: subItemType) => {
    const obj = {};
    switch (keyName) {
      case "orderId":
        Object.assign(obj, {
          justifyContent: "flex-start",
          fontWeight: 600,
          fontSize: "1.8rem",
          marginBottom: "0.6rem",
        });
        break;
      case "totalPaid":
      case "totalAmountPaid":
        Object.assign(obj, {
          fontWeight: 600,
        });
        break;
      case "time":
        Object.assign(obj, {
          fontWeight: 600,
          fontSize: "1.2rem",
          lineHeight: "1.2rem",
          color: "#92929F",
          marginBottom: "1.2rem",
        });
        break;
      case "cashbackRedeemed":
      case "freeAmount":
        Object.assign(obj, { color: "#E00051" });
        break;
      case "cashbackIssued":
        Object.assign(obj, { marginBottom: "2.4rem" });
        break;
      default:
        break;
    }
    return obj;
  };

  const otherStyleObj = {
    orderContent: {
      padding: "0 1.6rem",
      height: "calc(80vh - 5rem)",
      overflow: "auto",
    },
    emptyText: {
      height: "70%",
      color: "#92929F",
      fontWeight: 600,
      fontSize: "1.8rem",
    },
    loadingStyle: (page: number) => {
      const obj = {};
      if (page === 1) {
        Object.assign(obj, {
          height: "calc(80vh - 5rem)",
          display: "flex",
          alignItems: "center" as const,
          justifyContent: "center",
        });
      } else {
        Object.assign(obj, { height: "3rem", textAlign: "center" as const });
      }
      return obj;
    },
    orderTitle: {
      left: { display: "inline-block", marginBottom: "0.4rem" },
      right: { display: "inline-block", whiteSpace: "nowrap" as const },
    },
  };

  const handleClose = () => {
    setVisible(false);
    props.resetCardCount();
    trackMixPanel(MIX_PANEL_EVENT.ORDER_LIST_CLOSE, trackCommonData);
  };

  const handleScroll = (e: any) => {
    if (
      currentList.length >= totalCount ||
      isLoading ||
      e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight > 3
    ) {
      return;
    }
    setIsLoading(true);
    const targetPage = currentPage + 1;
    setCurrentPage(targetPage);
    setTimeout(() => {
      const beginIndex = (targetPage - 1) * pageSize;
      const endIndex = targetPage * pageSize;
      const appendData = orderListTotal.slice(beginIndex, endIndex);
      setCurrentList([...currentList, ...appendData]);
      setIsLoading(false);
    }, 500);
  };

  const getIdAndDate = (cur: any) => {
    const orderNum = cur?.attributes?.order_number;
    const idLast6 = orderNum ? orderNum.toUpperCase() : "";

    const stamp = cur?.attributes?.created_at;
    let curDate = "";
    let timestamp = 0;
    if (stamp) {
      const year = dayjs(stamp).year();
      const month = dayjs(stamp).month() + 1;
      const date = dayjs(stamp).date();
      if (!isNaN(year)) {
        curDate = `${month}/${date}/${year}`;
        timestamp = dayjs(stamp).valueOf();
      }
    }
    return { idLast6, curDate, timestamp };
  };

  const getOrderList = useCallback(() => {
    setIsLoading(true);

    fetchLoyaltyOrderAndTranscationList(props.lang, props.uuid)
      .then(([resultOrder, resultPay]) => {
        // data, originPrePayData
        const data =
          resultOrder.status === "fulfilled" ? resultOrder.value : { data: [] };
        const originPrePayData =
          resultPay.status === "fulfilled" ? resultPay.value : { data: [] };

        const prePayData = originPrePayData;

        setIsLoading(false);
        trackMixPanel(
          MIX_PANEL_EVENT.ORDER_LIST_SHOW_LOAD_SUCCESS,
          trackCommonData
        );
        const { data: prePayLists } = prePayData;
        const { data: lists } = data;

        // 充值和订单都 没有
        if (
          (!Array.isArray(lists) || lists.length === 0) &&
          (!Array.isArray(prePayLists) || prePayLists.length === 0)
        ) {
          setTotal(0);
          setOrderTotalList([]);
          return;
        }

        let finalList = [] as subItemKeyType[];
        // 有充值
        if (Array.isArray(prePayLists) && prePayLists.length > 0) {
          const resultData = prePayLists.reduce((arr, cur) => {
            const { idLast6, curDate, timestamp } = getIdAndDate(cur);

            const reChargeAmount = cur?.attributes?.cash_amount || "0";
            const freeAmount = cur?.attributes?.bonus_amount || "0";
            const totalAmountCredited = Math.floor(
              (parseFloat(reChargeAmount) * 1000 +
                parseFloat(freeAmount) * 1000) /
              1000
            ).toFixed(2);
            let bindRestaurant = getOrderBindRestaurant(cur?.attributes?.restaurant_id);

            arr.push({
              restaurantId: bindRestaurant.id,
              restaurantName: bindRestaurant.name,
              restaurantForeignName: bindRestaurant.foreign_name,
              orderId: idLast6,
              time: curDate,
              timestamp,
              orderType: PAY_TYPE,
              totalAmountPaid: reChargeAmount,
              totalAmountCredited,
            });
            return arr;
          }, [] as subItemKeyType[]);
          finalList.push(...resultData);
        }

        // 有订单
        if (Array.isArray(lists) && lists.length > 0) {
          // 有数据, 加到数据后面
          const resultData = lists.reduce((arr, cur) => {
            const { idLast6, curDate, timestamp } = getIdAndDate(cur);

            const billTotal = parseFloat(cur?.attributes?.billtotal) * 1000;
            const backRedeem = cur?.attributes?.cash_back_redeemed || "0";
            const cardRedeem = cur?.attributes?.card_credit_redeemed || "0";
            const cashbackRedeemed = Math.floor(
              parseFloat(backRedeem) * 1000 + parseFloat(cardRedeem) * 1000
            );

            let bindRestaurant = getOrderBindRestaurant(cur?.attributes?.restaurant_id);
            arr.push({
              restaurantId: bindRestaurant.id,
              restaurantName: bindRestaurant.name,
              restaurantForeignName: bindRestaurant.foreign_name,
              orderId: idLast6,
              orderType: ORDER_TYPE,
              time: curDate,
              timestamp,
              billTotal: (billTotal / 1000).toFixed(2),
              tips: cur?.attributes?.tips,
              cashbackRedeemed: (cashbackRedeemed / 1000).toFixed(2),
              totalPaid: cur?.attributes?.pay_total,
              cashbackIssued: cur?.attributes?.cash_back_issued,
              paymentType: cur?.attributes?.payment_type,
            });
            return arr;
          }, []);
          finalList.push(...resultData);
        }
        finalList.sort((a, b) => {
          const timeA = parseFloat(a.timestamp || "0");
          const timeB = parseFloat(b.timestamp || "0");
          return timeA < timeB ? 1 : -1;
        });
        setTotal(finalList.length);
        setOrderTotalList([...finalList]);
        setCurrentList(finalList.slice(0, pageSize));
      })
      .catch((e) => {
        setIsLoading(false);
        const errorMsg = getErrorMsg(e);
        message.error(errorMsg);
        trackMixPanel(MIX_PANEL_EVENT.ORDER_LIST_SHOW_LOAD_ERROR, {
          errorMsg,
          errorApi: `api/{lang}/v1/restaurant_members/{uuid}/orders`,
          ...trackCommonData,
        });
      });
  }, [props.lang, props.uuid, trackCommonData, fetchLoyaltyOrderAndTranscationList, getOrderBindRestaurant,]);

  useEffect(() => {
    if (props.clickCount === 0) {
      return;
    }
    // 每次打开都重新加载数据
    setCurrentPage(1);
    setCurrentList([]);
    setTotal(-1);
    setVisible(true);
    getOrderList();
  }, [props.clickCount, getOrderList]);

  // 获取高度
  const getListSize = () => {
    const ele = contentRef.current;
    if (!ele) {
      setBottomVisible(true);
      return;
    }
    // scrollHeight vs clientHeight
    const sHeight = (ele as HTMLDivElement).scrollHeight;
    const cHeight = (ele as HTMLDivElement).clientHeight;
    setBottomVisible(sHeight - cHeight > 0);
  };

  useEffect(() => {
    getListSize();
  }, [currentList]);

  // Update 'width' and 'height' when the window resizes
  useEffect(() => {
    window.addEventListener("resize", getListSize);
    return () => {
      window.removeEventListener("resize", getListSize);
    };
  }, []);

  return (
    <Drawer
      visible={visible}
      placement={"bottom"}
      forceRender={true}
      destroyOnClose={true}
      width={"100vw"}
      // 初始是 24.4 和
      height={"80vh"}
      closable={false}
      style={{ padding: 0, borderRadius: "1.2rem 1.2rem 0 0" }}
      bodyStyle={{ padding: 0 }}
      className={"drawClasses"}
    >
      <DrawTitle style={{ marginBottom: 0 }}>
        {/* <CloseOutlinedBox onClick={close} /> */}
        <CloseOutlinedBox src={closeIcon} onClick={handleClose} />
        <span style={{ fontSize: "1.8rem" }}>
          {OrderHistoryTitle[props.lang]}
        </span>
      </DrawTitle>
      {/* 订单内容 */}
      <div
        ref={contentRef}
        style={otherStyleObj.orderContent}
        onScroll={handleScroll}
      >
        {currentList.map((order, index) => {
          let { restaurantName, restaurantForeignName } = order;
          restaurantName = (props.lang === 'en' ? restaurantName : restaurantForeignName) || restaurantName;
          return (
            <OrderItem
              key={"order_" + index}
              isEndOrder={index === currentList.length - 1}
            >
              {Object.keys(order).map((item, orderIndex) => {
                if (["paymentType", "orderType", "timestamp", 'restaurantId', 'restaurantName', 'restaurantForeignName'].includes(item)) {
                  return null;
                }
                if (item === "time") {
                  return (
                    <div key={"orderI_" + orderIndex} style={styleObj("time")}>
                      <span>{OrderListDatas.time[props.lang]}</span>
                      <span>{order.time}</span>
                    </div>
                  );
                }
                return (
                  <DisBetween
                    key={"orderI_" + orderIndex}
                    style={styleObj(item as subItemType)}
                  >
                    {item === "orderId" ? (
                      <div>
                        <span style={otherStyleObj.orderTitle.left}>
                          {restaurantName}&nbsp;
                        </span>
                        <span style={otherStyleObj.orderTitle.right}>
                          {OrderListDatas.orderId[props.lang] + order.orderId}
                        </span>
                      </div>
                    ) : (
                      <div>
                        {OrderListDatas?.[item as subItemType][props.lang]}{" "}
                        {item === "totalPaid" &&
                          " - " +
                          (paymentTypeMap?.[
                            order.paymentType as paymentKeyType
                          ]?.[props.lang] || paymentTypeMap.card[props.lang])}
                      </div>
                    )}
                    <div>
                      <span>
                        {item === "cashbackRedeemed" &&
                          OrderListDatas?.[item].prefix}
                        {item === "freeAmount" && OrderListDatas?.[item].prefix}
                        {item !== "orderId" && "$"}
                      </span>
                      <span>
                        <span>
                          {item !== "orderId" ? (
                            <span>{`${order[item as subItemType]}`}</span>
                          ) : null}
                        </span>
                      </span>
                    </div>
                  </DisBetween>
                );
              })}
            </OrderItem>
          );
        })}
        {isLoading && (
          <div style={otherStyleObj.loadingStyle(currentPage)}>
            <Spin
              indicator={
                <LoadingOutlined style={{ fontSize: "2.4rem" }} spin />
              }
            />
          </div>
        )}
        {totalCount === 0 && (
          <DisplayText style={otherStyleObj.emptyText}>
            {orderListEmpty[props.lang]}
          </DisplayText>
        )}
        {bottomVisible &&
          totalCount > 0 &&
          currentList.length >= totalCount && (
            <Divider plain>{orderListTip[props.lang]}</Divider>
          )}
      </div>
    </Drawer>
  );
};

const OrderItem = styled.div<{
  isEndOrder: boolean;
}>`
  border-bottom: ${(props) =>
    !props.isEndOrder ? "1px solid #c0c2cc" : "none"};
  margin-top: 1.6rem;
`;

const DisBetween = styled(DisplayText)`
  justify-content: space-between;
  line-height: 1.8rem;
  font-size: 1.4rem;
  color: #050707;
  margin-bottom: 1.2rem;
`;
